import React, { useMemo } from 'react';

import { formatCurrency } from '~libs/dataUtils';
import { getErrorMessage } from '~libs/axiosWrapper';

import { ClickableIcon, ErrorResponse, Modal, Spinner } from '~components';
import { faEye } from '~components/icon';
import { Column, TableView } from '~components/table-view';

import PaymentStatusBadge from '~direct-debits/components/payment-batches/PaymentStatusBadge';

import { useGetPaymentBatchClientInvoicesQuery } from '~direct-debits/actions/paymentBatches';
import { formatBatchInvoiceDate } from '~direct-debits/lib/formatBatchDate';
import { PaymentBatchInvoice } from '~direct-debits/models';

interface ComponentProps {
  isOpen: boolean;
  batchId: string | null;
  // The user-facing `DDB-NNNNNN` from `payment_batches.reference`. Shown in
  // the header so a customer-support operator can read the same identifier
  // they see in GoCardless / on the customer's bank statement.
  batchReference?: string | null;
  clientId: number | null;
  clientName?: string | null;
  onClose: () => void;
  onViewInvoice: (invoiceId: string) => void;
}

const formatInvoiceIdShort = (id: string): string => {
  if (!id) return '-';
  return id.length > 8 ? `${id.slice(0, 8)}...` : id;
};

/**
 * The invoices behind one client's payment line, as a dialog stacked over the
 * batch-detail dialog. A dialog rather than a panel expanded under the client
 * row: the clients table is as long as the batch has clients, so an inline
 * panel opens further down the page the further down the operator clicked, and
 * on a longer batch it can open below the fold and read as nothing having
 * happened. A dialog always lands in the same place.
 *
 * Reactstrap portals each dialog into its own container appended to the body,
 * so this is a DOM sibling of the batch dialog rather than nested inside it.
 * The consequences worth knowing: only the last-opened dialog handles Escape
 * and Tab cycling (reactstrap gates both on its own modal index), the body
 * stays scroll-locked for the batch dialog when this one closes, and closing
 * returns focus to the row action that opened it.
 */
const ClientInvoicesModal: React.FC<ComponentProps> = ({
  isOpen,
  batchId,
  batchReference,
  clientId,
  clientName,
  onClose,
  onViewInvoice,
}) => {
  const skip = !isOpen || batchId === null || clientId === null;
  const { data, isFetching, isError, error } = useGetPaymentBatchClientInvoicesQuery(
    { batch_id: batchId ?? '', client_id: clientId ?? 0 },
    { skip },
  );

  const rows = useMemo(() => (data?.rows ?? []).map((invoice) => ({ ...invoice })), [data?.rows]);

  // Inline column factory keeps the dialog self-contained - the columns include
  // a row-action icon that escalates to the parent so the invoice record can
  // be opened.
  const columns: Column[] = useMemo(
    () => [
      {
        id: 'reference',
        header: 'Invoice',
        type: 'text',
        deriveValue: (row) => (row.reference as string) || formatInvoiceIdShort(row.id as string),
      },
      {
        id: 'invoice_date',
        header: 'Date',
        type: 'text',
        deriveValue: (row) => formatBatchInvoiceDate(row.invoice_date as string),
        classNames: { header: 'cell-align-center', column: 'cell-align-center' },
      },
      {
        id: 'amount',
        header: 'Amount',
        type: 'text',
        deriveValue: (row) => formatCurrency(row.amount as string),
        classNames: { header: 'cell-align-right', column: 'cell-align-right' },
      },
      {
        id: 'status',
        header: 'Status',
        type: 'custom',
        component: PaymentStatusBadge,
        classNames: { header: 'cell-align-center', column: 'cell-align-center' },
      },
      {
        id: 'actions',
        header: '',
        type: 'custom',
        component: ({ row }: { row: PaymentBatchInvoice }) => (
          <div className="d-flex gap-2 justify-content-end align-items-center">
            <ClickableIcon
              id={`dd-payment-batch-invoice-view-${row.id}`}
              dataCy={`dd-payment-batch-invoice-view-${row.id}`}
              icon={faEye}
              toolTip="View Invoice"
              ariaLabel="View Invoice"
              onClick={() => onViewInvoice(row.id)}
            />
          </div>
        ),
        classNames: { header: 'cell-align-right', column: 'cell-align-right' },
      },
    ],
    [onViewInvoice],
  );

  let headerLabel: string;
  if (!clientName) {
    headerLabel = 'Client Invoices';
  } else if (batchReference) {
    headerLabel = `${clientName} - Batch ${batchReference}`;
  } else {
    headerLabel = clientName;
  }

  // Wires `aria-labelledby` from the dialog to its header text - the reactstrap
  // Modal otherwise has no accessible name.
  const headerId = 'dd-payment-batch-invoices-modal-header';

  // Closing by any route (the header X, the backdrop, Escape, or the footer
  // Close button) dismisses only this dialog and leaves the batch dialog open
  // behind it.
  const setIsOpen = (open: boolean) => {
    if (!open) onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      size="lg"
      scrollable
      header={<span id={headerId}>{headerLabel}</span>}
      headerClassName="header"
      labelledBy={headerId}
      data-cy="dd-payment-batch-invoices-modal"
    >
      <div data-cy="dd-payment-batch-invoices-modal-body">
        {isError ? (
          <ErrorResponse message={getErrorMessage(error)} />
        ) : (
          <>
            {isFetching && !data && (
              <div className="my-3">
                <Spinner loading />
              </div>
            )}
            <TableView
              id="dd-payment-batch-invoices-table"
              columns={columns}
              data={rows}
              isLoading={isFetching}
              emptyMessage="No invoices for this client in this batch"
              readonly
              striped
            />
          </>
        )}
      </div>
    </Modal>
  );
};

export default ClientInvoicesModal;
